import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { ApiError } from "../types";

const API_URL = import.meta.env.VITE_API_URL;

export function clearNFTRedisCache() {
	return fetch(`${API_URL}/nfts/cache`, {
		method: "DELETE",
		credentials: "include",
		headers: {
			"Content-Type": "application/json",
		},
	});
}

type TRefreshBody = {
	collectionAddress: string;
	tokenIds: number[];
};

type TPurgeBody = {
	keys: string[];
};

export function purgeNFTAlchemyCache(body: TRefreshBody) {
	return fetch(`${API_URL}/nfts/refresh`, {
		method: "POST",
		credentials: "include",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(body),
	});
}

export function purgeCdnCache(body: TPurgeBody) {
	return fetch(`${API_URL}/inventory/purge`, {
		method: "POST",
		credentials: "include",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(body),
	});
}

export const useClearCacheMutation = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			const response = await clearNFTRedisCache();

			if (!response.ok) {
				const error = await response.json();
				throw error;
			}

			return response.json();
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["nfts"],
			});
			toast.success("Cache cleared successfully");
		},
		onError: (error) => {
			toast.error(error.message.slice(0, 300));
		},
	});
};

export const usePurgeAlchemyCacheMutation = () => {
	return useMutation<null, ApiError, TRefreshBody>({
		mutationFn: async (data) => {
			const response = await purgeNFTAlchemyCache(data);

			if (!response.ok) {
				const error = await response.json();
				throw error;
			}

			return response.json();
		},
	});
};
